import { useEffect, useState } from 'react';
import { skillsApi } from '../../../api/talent.js';
import { hrApi } from '../../../api/hr.js';

/**
 * Skill-gap view — an employee vs a role's required skills (taxonomy skills
 * whose category == the role). Shows met / partial / missing per skill.
 */
export default function SkillGap() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [role, setRole] = useState('Backend Engineer');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    hrApi.listEmployees().then((e) => setEmployees(e?.items || e || [])).catch(() => setEmployees([]));
  }, []);

  async function run(e) {
    e.preventDefault();
    if (!employeeId || !role) return;
    setLoading(true);
    setError(null);
    try {
      setResult(await skillsApi.gap({ employee_id: employeeId, role }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to run gap analysis');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section data-testid="talent-gap-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Skill Gap</h1>
          <p className="muted">Compare an employee against a role's required skills</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="talent-gap-error">{error}</div>}

      <form onSubmit={run} className="card form-card grid-form">
        <label>Employee
          <select data-testid="talent-gap-employee-input" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} required>
            <option value="" disabled>— select —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
        </label>
        <label>Role<input data-testid="talent-gap-role-input" value={role} onChange={(e) => setRole(e.target.value)} required /></label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="talent-gap-run">Run gap analysis</button></div>
      </form>

      {result && (
        <div className="card" data-testid="talent-gap-result">
          <h3>
            {result.employee?.employee_name || 'Employee'} vs {result.role}
            {' — '}
            <span data-testid="talent-gap-summary">
              {result.metCount}/{result.requiredCount} met
              {result.metAllRequired ? ' ✓ fully qualified' : ` · ${result.gapCount} gap(s)`}
            </span>
          </h3>
          <table className="data-table" data-testid="talent-gap-list">
            <thead><tr><th>Skill</th><th>Target</th><th>Current</th><th>Deficit</th><th>Status</th></tr></thead>
            <tbody>
              {result.gaps.map((g) => (
                <tr key={g.skillId} data-testid={`talent-gap-row-${g.skillId}`}>
                  <td>{g.name || g.skillId}</td>
                  <td>{g.target}</td>
                  <td>{g.current}</td>
                  <td>{g.deficit}</td>
                  <td><span className="chip">{g.met ? 'met' : g.has ? 'below target' : 'missing'}</span></td>
                </tr>
              ))}
              {result.gaps.length === 0 && <tr><td colSpan={5} className="muted">No required skills defined for this role.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
